import { GeneralReportEntity } from "domain/entity/GeneralReport/GeneralReportEntity";
import _ from "lodash";
import { ActiveIndDroOpts } from "presentation/constant/DropDownOptions/Common/ActiveIndDroOpts";
import { TransactionTypeDroOpts } from "presentation/constant/DropDownOptions/Document/TransactionTypeDroOpts";
import { approvalProcessStatusDropdownOption, docTypeDropdownOption, ediViewDropdownOption, monthDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { GeneralReportConstant } from "presentation/constant/GeneralReport/GeneralReportConstant";
import { proposalTypeRadioOptions, versionTypeRadioOptions } from "presentation/constant/RadioOptions/StaticRadioOptions";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useGeneralReportVM } from "presentation/hook/GeneralReport/useGeneralReportVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useGeneralReportTracked } from "presentation/store/GeneralReport/GeneralReportProvider";
import { isActiveIndShowOptional, isCompanyCodeShowOptional, isDateFromToShowOptional, isDocDateShowOptional, processReportMissingFields, reportCheckFieldMandatory } from "presentation/utils/report/ReportCheckFieldUtils";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import DateTimePickerComponent from "presentation/view/components/DateTimePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHGroupRadioButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const GeneralReportPanel = () => {
    const GENERAL_REPORT_CONSTANT = GeneralReportConstant.Table;
    const [generalReportState] = useGeneralReportTracked();
    const generalReportVM = useGeneralReportVM();
    const [allFormState, setAllFormState] = useState<{ [key: string]: string }>({});
    const [isInitPanelData, setIsInitPanelData] = useState(true);
    const { currentReport, criteriaTypeIdMap, reportIdTypeMap } = generalReportState;
    // const [isUseStatic, setIsUseStatic] = useState(false);    
    const messageBarVM = useMessageBarVM();
    const [isLoading, setIsLoading] = useState(false);
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowRead } = anaInfoState;

    useEffect(() => {
        const refreshCriteriaName = async () => {
            (currentReport.reportType === "Criteria Checklist" && currentReport.criteriaType) && generalReportVM.onCriteriaTypeChanged(criteriaTypeIdMap[currentReport.criteriaType]);
        }
        refreshCriteriaName();
    }, [generalReportVM, currentReport.reportType, currentReport.criteriaType, criteriaTypeIdMap])

    useEffect(() => {
        if (currentReport.reportType) {
            const newFormState = processReportMissingFields(currentReport)

            setAllFormState(prev => ({
                ...prev,
                ...newFormState
            }));
        }
    }, [currentReport])

    const handleReset = useCallback(() => {
        setAllFormState({})
        generalReportVM.onResetClick(operatingUnit);
    }, [generalReportVM, operatingUnit]);

    const checkFieldMandatory = useCallback((currentReport: GeneralReportEntity, operatingUnit: string) => {
        let ret = reportCheckFieldMandatory(currentReport, operatingUnit);

        if (!currentReport.reportType) {
            ret = { ...ret, reportType: "Missing", /*mandatoryCheckFail: "Please input the missing value."*/ };
        }
        if (!_.isEmpty(ret)) {
            setAllFormState(ret);
            messageBarVM.showError(ret["mandatoryCheckFail"] || ret["validateFailed"]);
            return true;
        }
        return false;
    }, [messageBarVM]);

    const isFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Proposal Master Report") {
            if (currentReport.proposalType === "GN") return true
        }
        if (currentReport.reportType === "Pre-Cool Without Vessel Information") {
            return true
        }
        if (currentReport.reportType === "Receival and Delivery Charges Summary") {
            return true
        }
        if (currentReport.reportType === "Volume Discount Report") {
            return true
        }
        if (currentReport.reportType === "T3 Invoice Revenue Report") {
            return true
        }

        return false;
    }, [currentReport.proposalType, currentReport.reportType]);

    const isDateFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Pre-Cool Without Vessel Information") {
            return true
        }
        if (currentReport.reportType === "T3 Invoice Revenue Report") {
            return true
        }

        return false;
    }, [currentReport.reportType]);

    const isCompanyCodeFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Invoice Summary Report") {
            return true
        }
        if (currentReport.reportType === "Proposal Master Report") {
            return true
        }
        if (currentReport.reportType === "Throughput by Company") {
            return true
        }
        if (currentReport.reportType === "Company Master Report") {
            return true
        }
        if (currentReport.reportType === "Credit Note Summary") {
            return true
        }
        if (currentReport.reportType === "Invoice Register") {
            return true
        }
        if (currentReport.reportType === "Let Pass Account Monthly Statement") {
            return true
        }

        return false;
    }, [currentReport.reportType]);

    const isConsortiumFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Volume Discount Report") {
            return true
        }
        return false;
    }, [currentReport.reportType]);

    const isConsortiumSpecialFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Container Un-Billed / Billed THC Report") {
            return true
        }
        if (currentReport.reportType === "Invoice Summary Report") {
            return true
        }
        if (currentReport.reportType === "Receival and Delivery Charges Summary") {
            return true
        }

        return false;
    }, [currentReport.reportType]);

    const isActiveIndFieldDisable = useCallback(() => {
        if (currentReport.reportType === "Company Master Report") {
            return true
        }
        return false;
    }, [currentReport.reportType]);

    const isDateTime = useCallback(() => {
        if (currentReport.reportType === "LPA Daily Deposit Report") {
            return true
        }
        return false;
    }, [currentReport.reportType]);

    const isSpecialFieldDisable = useCallback(() => {

        if (currentReport.reportType === "LPA Account Analysis") {
            return true
        }
        if (currentReport.reportType === "LPA Daily Deposit Report") {
            return true
        }
        if (currentReport.reportType === "Credit Note Summary") {
            return true
        }
        if (currentReport.reportType === "Dayend Report") {
            return true
        }
        if (currentReport.reportType === "Empty Container Summary Report") {
            return true
        }
        if (currentReport.reportType === "Empty Pool Container List Report") {
            return true
        }
        if (currentReport.reportType === "Tariff item Mapping List") {
            return true
        }
        if (currentReport.reportType === "Throughput by Company") {
            return true
        }
        if (currentReport.reportType === "Throughput By Line Report") {
            return true
        }
        if (currentReport.reportType === "Full Container Summary Report") {
            return true
        }

        return false;
    }, [currentReport.reportType]);

    const isCompanyTypeAndTypeShowOptional = useCallback(() => {
        let ret = true;
        if (currentReport.reportType === "CC Invoice/Credit Note Summary Report") {
            return false
        }
        if (currentReport.reportType === "Credit Notes Summary Report") {
            return false
        }
        if (currentReport.reportType === "Sales Tax Report") {
            return false
        }

        return ret;
    }, [currentReport.reportType]);

    const handlePrint = useCallback(() => {
        if (checkFieldMandatory(currentReport, operatingUnit)) return;
        setIsLoading(true);
        generalReportVM.onPrintClick(reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Report is sent to printers successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Report print failed.");
            }
        });
    }, [checkFieldMandatory, currentReport, operatingUnit, generalReportVM, reportIdTypeMap, messageBarVM]);

    const handleExportClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport, operatingUnit)) return;
        setIsLoading(true);
        generalReportVM.onExportClick(exportType, reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Export successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Export failure.");
            }
        });
    }, [checkFieldMandatory, currentReport, operatingUnit, generalReportVM, reportIdTypeMap, messageBarVM]);

    const handleView = useCallback(() => {
        if (checkFieldMandatory(currentReport, operatingUnit)) return;
        setIsLoading(true);
        generalReportVM.onViewClick(reportIdTypeMap, currentReport).then(data => {
            if (data === "true") {
                setIsLoading(false);
            } else if (data === "false") {
                setIsLoading(false);
                messageBarVM.showError("View " + currentReport.reportType + " failure.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data);
            }
        });
    }, [checkFieldMandatory, currentReport, generalReportVM, messageBarVM, operatingUnit, reportIdTypeMap]);


    const memoExportMenu = useMemo(() => {
        const exportTypes = [];

        exportTypes.push({
            title: GeneralReportConstant.Title.PDF,
            disabled: false,
            command: () => {
                handleExportClick(GeneralReportConstant.Title.PDF);
            }
        });
        // if (currentReport.reportType !== "Cash Reconciliation") {
        exportTypes.push({
            title: GeneralReportConstant.Title.CSV,
            disabled: false,
            command: () => {
                handleExportClick(GeneralReportConstant.Title.CSV);
            }
        });

        exportTypes.push({
            title: GeneralReportConstant.Title.RTF,
            disabled: false,
            command: () => {
                handleExportClick(GeneralReportConstant.Title.RTF);
            }
        });
        // }
        return exportTypes;
    }, [handleExportClick])

    const handleDownloadClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport, operatingUnit)) return;
        setIsLoading(true);
        generalReportVM.onDownloadClick(reportIdTypeMap, currentReport, exportType).then(data => {
            if (data === "success") {
                messageBarVM.showSuccess("Download successfully.");
                setIsLoading(false);
            } else {
                messageBarVM.showError(data);
                setIsLoading(false);
            }
        });
    }, [checkFieldMandatory, currentReport, generalReportVM, messageBarVM, operatingUnit, reportIdTypeMap]);

    const memoDownloadMenu = useMemo(() => {
        return [
            {
                title: GeneralReportConstant.Title.CSV,
                disabled: false,
                command: () => {
                    handleDownloadClick(GeneralReportConstant.Title.CSV);
                }
            },
            {
                title: GeneralReportConstant.Title.XLS,
                disabled: false,
                command: () => {
                    handleDownloadClick(GeneralReportConstant.Title.XLS);
                }
            },
            {
                title: GeneralReportConstant.Title.PDF,
                disabled: false,
                command: () => {
                    handleDownloadClick(GeneralReportConstant.Title.PDF);
                }
            }
        ]
    }, [handleDownloadClick])

    const onReportTypeChange = useCallback((fieldKey: string, fieldValue: IFieldValue) => {
        setAllFormState({});

        generalReportVM.onReportTypeChanged(fieldKey, fieldValue, operatingUnit);
    }, [generalReportVM, operatingUnit]);

    /*const onHeaderFieldChange = useCallback((fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {        
        generalReportVM.onHeaderFieldChange(fieldKey, fieldValue, fFullValue);
    }, [generalReportVM]); */

    const memoCompanyCodeDroOptions = useMemo(() => {
        if (currentReport.reportType === "Credit Note Summary" || currentReport.reportType === "Proposal Master Report"
            || currentReport.reportType === "Invoice Register" || currentReport.reportType === "Throughput By Line Report"
        ) {
            return generalReportState.dynamicOptions.allCompanyCodeDropdownOptions;
        } else if (currentReport?.reportType === "Volume Discount Report") {
            return [];
        }

        return generalReportState.dynamicOptions.companyCodeDropdownOptions;
    }, [currentReport.reportType, generalReportState.dynamicOptions.allCompanyCodeDropdownOptions, generalReportState.dynamicOptions.companyCodeDropdownOptions]);

    const memoChargeOnCompDroOptions = useMemo(() => {
        if (currentReport.reportType === "Throughput By Line Report") {
            return generalReportState.dynamicOptions.allCompanyCodeDropdownOptions;
        }

        return generalReportState.dynamicOptions.companyCodeDropdownOptions;
    }, [currentReport.reportType, generalReportState.dynamicOptions.allCompanyCodeDropdownOptions, generalReportState.dynamicOptions.companyCodeDropdownOptions]);

    const memoProposalNoFromDroOptions = useMemo(() => {
        const groupOfProposalNoOptions = generalReportState.dynamicOptions.proposalNoDropdownOptions[currentReport.companyCodeFrom ?? ""];
        const commonProposalNoOptions = _.orderBy(_.uniqBy(_.flatMap(_.concat(groupOfProposalNoOptions ?? [])), 'value'), 'dropdownLabel');

        return commonProposalNoOptions;
    }, [currentReport.companyCodeFrom, generalReportState.dynamicOptions.proposalNoDropdownOptions]);

    const memoProposalNoToDroOptions = useMemo(() => {
        const groupOfProposalNoOptions = generalReportState.dynamicOptions.proposalNoDropdownOptions[currentReport.companyCodeTo ?? ""];
        const commonProposalNoOptions = _.orderBy(_.uniqBy(_.flatMap(_.concat(groupOfProposalNoOptions ?? [])), 'value'), 'dropdownLabel');

        return commonProposalNoOptions;
    }, [currentReport.companyCodeTo, generalReportState.dynamicOptions.proposalNoDropdownOptions]);

    useEffect(() => {
        if (isInitPanelData) {
            setIsInitPanelData(false);
            onReportTypeChange("reportType", currentReport.reportType)
        }
    }, [currentReport.reportType, isInitPanelData, onReportTypeChange])

    const memoReportType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "630px", marginBottom: "20px", marginTop: "10px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["reportType"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.reportType ?? ''}
                fieldValue={currentReport.reportType}
                fieldLabel={GENERAL_REPORT_CONSTANT.REPORT_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'reportType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.reportTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    onReportTypeChange(fieldKey, fieldValue && fieldValue.toString())} />
        </div>
        , [allFormState, currentReport.reportType, GENERAL_REPORT_CONSTANT.REPORT_TYPE, generalReportState.dynamicOptions.reportTypeDropdownOptions, onReportTypeChange])

    const memoPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["printer"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.printer ?? ''}
                fieldValue={currentReport.printer}
                fieldLabel={GENERAL_REPORT_CONSTANT.PRINTER}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'printer'}
                maxLength={60}
                options={generalReportState.dynamicOptions.printQueueDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString())} />
        </div>
        , [allFormState, currentReport.printer, GENERAL_REPORT_CONSTANT.PRINTER, generalReportState.dynamicOptions.printQueueDropdownOptions, generalReportVM])

    const memoFileLocation = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["fileLocation"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.fileLocation ?? ''}
                fieldValue={currentReport.fileLocation}
                fieldLabel={GENERAL_REPORT_CONSTANT.FILE_LOCATION}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'fileLocation'}
                maxLength={60}
                options={generalReportState.dynamicOptions.fileLocationDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.fileLocation, GENERAL_REPORT_CONSTANT.FILE_LOCATION, generalReportState.dynamicOptions.fileLocationDropdownOptions, generalReportVM])

    const memoEdiView = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.ediView ?? ''}
                fieldValue={currentReport?.ediView}
                fieldLabel={GENERAL_REPORT_CONSTANT.EDI_VIEW}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'ediView'}
                maxLength={60}
                options={ediViewDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.ediView, GENERAL_REPORT_CONSTANT.EDI_VIEW, generalReportVM])

    const memoCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyCode"] : ""}
                isReadOnly={false}
                isShowOptional={isCompanyCodeShowOptional(currentReport.reportType)}
                readOnlyValue={currentReport?.companyCode ?? ''}
                fieldValue={currentReport?.companyCode}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_CODE}
                isSaveClicked={false}
                fieldType={currentReport?.reportType === "Volume Discount Report" ? FieldType.TEXT : FieldType.DROPDOWN}
                fieldKey={'companyCode'}
                maxLength={10}
                isLimitOptions={true}
                options={memoCompanyCodeDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onFieldChangeUpperCase(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.reportType, currentReport?.companyCode, GENERAL_REPORT_CONSTANT.COMPANY_CODE, memoCompanyCodeDroOptions, generalReportVM])

    const memoCompanyCodeFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyCodeFrom"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.companyCodeFrom ?? ''}
                fieldValue={currentReport?.companyCodeFrom}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_CODE_FROM}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyCodeFrom'}
                maxLength={60}
                isLimitOptions={true}
                options={memoCompanyCodeDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.companyCodeFrom, GENERAL_REPORT_CONSTANT.COMPANY_CODE_FROM, memoCompanyCodeDroOptions, generalReportVM])

    const memoCompanyCodeTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyCodeTo"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.companyCodeTo ?? ''}
                fieldValue={currentReport?.companyCodeTo}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_CODE_TO}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyCodeTo'}
                maxLength={60}
                isLimitOptions={true}
                options={memoCompanyCodeDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.companyCodeTo, GENERAL_REPORT_CONSTANT.COMPANY_CODE_TO, memoCompanyCodeDroOptions, generalReportVM])

    const memoProposalFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["proposalFrom"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.proposalFrom ?? ''}
                fieldValue={currentReport?.proposalFrom}
                fieldLabel={GENERAL_REPORT_CONSTANT.PROPOSAL_FROM}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'proposalFrom'}
                maxLength={60}
                options={memoProposalNoFromDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.proposalFrom, GENERAL_REPORT_CONSTANT.PROPOSAL_FROM, memoProposalNoFromDroOptions, generalReportVM])

    const memoProposalTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["proposalTo"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.proposalTo ?? ''}
                fieldValue={currentReport?.proposalTo}
                fieldLabel={GENERAL_REPORT_CONSTANT.PROPOSAL_TO}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'proposalTo'}
                maxLength={60}
                options={memoProposalNoToDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.proposalTo, GENERAL_REPORT_CONSTANT.PROPOSAL_TO, memoProposalNoToDroOptions, generalReportVM])

    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.chargeOnCompany ?? ''}
                fieldValue={currentReport?.chargeOnCompany}
                fieldLabel={GENERAL_REPORT_CONSTANT.CHARGE_ON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeOnCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={memoChargeOnCompDroOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.chargeOnCompany, GENERAL_REPORT_CONSTANT.CHARGE_ON_COMPANY, memoChargeOnCompDroOptions, generalReportVM])

    const memoActiveInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["activeInd"] : ""}
                isReadOnly={false}
                isShowOptional={isActiveIndShowOptional(currentReport.reportType)}
                readOnlyValue={currentReport?.activeInd ?? ''}
                fieldValue={currentReport?.activeInd}
                fieldLabel={GENERAL_REPORT_CONSTANT.ACTIVE_IND}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'activeInd'}
                maxLength={60}
                options={ActiveIndDroOpts().getDroOptsModel()}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.reportType, currentReport?.activeInd, GENERAL_REPORT_CONSTANT.ACTIVE_IND, generalReportVM])


    const memoChargeType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.chargeType ?? ''}
                fieldValue={currentReport?.chargeType}
                fieldLabel={GENERAL_REPORT_CONSTANT.CHARGE_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.chargeTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.chargeType, GENERAL_REPORT_CONSTANT.CHARGE_TYPE, generalReportState.dynamicOptions.chargeTypeDropdownOptions, generalReportVM])

    const memoMasterCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.masterCompanyCode ?? ''}
                fieldValue={currentReport?.masterCompanyCode}
                fieldLabel={GENERAL_REPORT_CONSTANT.MASTER_COMPANY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'masterCompanyCode'}
                maxLength={60}
                isLimitOptions={true}
                options={generalReportState.dynamicOptions.masterCompanyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.masterCompanyCode, GENERAL_REPORT_CONSTANT.MASTER_COMPANY_CODE, generalReportState.dynamicOptions.masterCompanyCodeDropdownOptions, generalReportVM])

    const memoCustomerCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.customerCode ?? ''}
                fieldValue={currentReport?.customerCode}
                fieldLabel={GENERAL_REPORT_CONSTANT.CUSTOMER_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'customerCode'}
                maxLength={60}
                options={generalReportState.dynamicOptions.customerCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.customerCode, GENERAL_REPORT_CONSTANT.CUSTOMER_CODE, generalReportState.dynamicOptions.customerCodeDropdownOptions, generalReportVM])

    const memoCriteriaType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.criteriaType ?? ''}
                fieldValue={currentReport?.criteriaType}
                fieldLabel={GENERAL_REPORT_CONSTANT.CRITERIA_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'criteriaType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.criteriaTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.criteriaType, GENERAL_REPORT_CONSTANT.CRITERIA_TYPE, generalReportState.dynamicOptions.criteriaTypeDropdownOptions, generalReportVM])

    const memoCriteriaName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.criteriaName ?? ''}
                fieldValue={currentReport?.criteriaName}
                fieldLabel={GENERAL_REPORT_CONSTANT.CRITERIA_NAME}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'criteriaName'}
                maxLength={60}
                options={generalReportState.dynamicOptions.criteriaNameDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.criteriaName, GENERAL_REPORT_CONSTANT.CRITERIA_NAME, generalReportState.dynamicOptions.criteriaNameDropdownOptions, generalReportVM])

    const memoProposalNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.proposalNo ?? ''}
                fieldValue={currentReport?.proposalNo}
                fieldLabel={GENERAL_REPORT_CONSTANT.PROPOSAL_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'proposalNo'}
                maxLength={7}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.proposalNo, GENERAL_REPORT_CONSTANT.PROPOSAL_NO, generalReportVM])

    const memoDocNoFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.docNoFrom ?? ''}
                fieldValue={currentReport?.docNoFrom}
                fieldLabel={GENERAL_REPORT_CONSTANT.DOC_NO_FROM}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'docNoFrom'}
                maxLength={15}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DOC_NO_FROM, generalReportVM, currentReport?.docNoFrom])

    const memoDocNoTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.docNoTo ?? ''}
                fieldValue={currentReport?.docNoTo}
                fieldLabel={GENERAL_REPORT_CONSTANT.DOC_NO_TO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'docNoTo'}
                maxLength={15}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DOC_NO_TO, generalReportVM, currentReport?.docNoTo])

    const memoDocDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={isDocDateShowOptional(currentReport.reportType)}
                label={GENERAL_REPORT_CONSTANT.DOC_DATE_FROM}
                width="300px"
                date={currentReport?.docDateFrom}
                fieldName="docDateFrom"
                errorMessage={allFormState ? allFormState["docDateFrom"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DOC_DATE_FROM, allFormState, currentReport?.docDateFrom, currentReport.reportType, generalReportVM.onHeaderFieldChange])

    const memoDocDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={isDocDateShowOptional(currentReport.reportType)}
                label={GENERAL_REPORT_CONSTANT.DOC_DATE_TO}
                width="300px"
                date={currentReport?.docDateTo}
                fieldName="docDateTo"
                errorMessage={allFormState ? allFormState["docDateTo"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DOC_DATE_TO, allFormState, currentReport?.docDateTo, currentReport.reportType, generalReportVM.onHeaderFieldChange])

    const memoGlDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={GENERAL_REPORT_CONSTANT.GL_DATE_FROM}
                width="300px"
                date={currentReport?.glDateFrom}
                fieldName="glDateFrom"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.GL_DATE_FROM, currentReport?.glDateFrom, generalReportVM.onHeaderFieldChange])

    const memoGlDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={GENERAL_REPORT_CONSTANT.GL_DATE_TO}
                width="300px"
                date={currentReport?.glDateTo}
                fieldName="glDateTo"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.GL_DATE_TO, currentReport?.glDateTo, generalReportVM.onHeaderFieldChange])

    const memoOpsDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.OPS_DATE_FROM}
                width="300px"
                optional={true}
                date={currentReport?.opsDateFrom}
                fieldName="opsDateFrom"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.OPS_DATE_FROM, currentReport?.opsDateFrom, generalReportVM.onHeaderFieldChange])

    const memoOpsDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.OPS_DATE_TO}
                width="300px"
                optional={true}
                date={currentReport?.opsDateTo}
                fieldName="opsDateTo"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.OPS_DATE_TO, currentReport?.opsDateTo, generalReportVM.onHeaderFieldChange])

    const memoEffectiveDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_FROM}
                width="300px"
                optional={true}
                date={currentReport?.effectiveDateFrom}
                fieldName="effectiveDateFrom"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_FROM, currentReport?.effectiveDateFrom, generalReportVM.onHeaderFieldChange])

    const memoEffectiveDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_TO}
                width="300px"
                optional={true}
                date={currentReport?.effectiveDateTo}
                fieldName="effectiveDateTo"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EFFECTIVE_DATE_TO, currentReport?.effectiveDateTo, generalReportVM.onHeaderFieldChange])

    const memoExpiryDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EXPIRY_DATE_FROM}
                width="300px"
                optional={true}
                date={currentReport?.expiryDateFrom}
                fieldName="expiryDateFrom"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EXPIRY_DATE_FROM, currentReport?.expiryDateFrom, generalReportVM.onHeaderFieldChange])

    const memoExpiryDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EXPIRY_DATE_TO}
                width="300px"
                optional={true}
                date={currentReport?.expiryDateTo}
                fieldName="expiryDateTo"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EXPIRY_DATE_TO, currentReport?.expiryDateTo, generalReportVM.onHeaderFieldChange])

    const memoExceptionDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_FROM}
                width="300px"
                optional={true}
                date={currentReport?.exceptionDateFrom}
                fieldName="exceptionDateFrom"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_FROM, currentReport?.exceptionDateFrom, generalReportVM.onHeaderFieldChange])

    const memoExceptionDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_TO}
                width="300px"
                optional={true}
                date={currentReport?.exceptionDateTo}
                fieldName="exceptionDateTo"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.EXCEPTION_DATE_TO, currentReport?.exceptionDateTo, generalReportVM.onHeaderFieldChange])

    const memoExceptionType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["exceptionType"] : ""}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.exceptionType ?? ''}
                fieldValue={currentReport?.exceptionType}
                fieldLabel={GENERAL_REPORT_CONSTANT.EXCEPTION_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'exceptionType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.exceptionTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.exceptionType, GENERAL_REPORT_CONSTANT.EXCEPTION_TYPE, generalReportState.dynamicOptions.exceptionTypeDropdownOptions, generalReportVM])

    const memoYear = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["year"] : ""}
                isReadOnly={false}
                isShowOptional={(operatingUnit === "SAPT" || operatingUnit === "KICT") ? true : false}
                readOnlyValue={currentReport?.year ?? ''}
                fieldValue={currentReport?.year}
                fieldLabel={GENERAL_REPORT_CONSTANT.YEAR}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'year'}
                maxLength={4}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.YEAR, allFormState, currentReport?.year, generalReportVM, operatingUnit])

    const memoMonth = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["month"] : ""}
                isReadOnly={false}
                isShowOptional={(operatingUnit === "SAPT" || operatingUnit === "KICT") ? true : false}
                readOnlyValue={currentReport?.month ?? ''}
                fieldValue={currentReport?.month}
                fieldLabel={GENERAL_REPORT_CONSTANT.MONTH}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'month'}
                maxLength={60}
                options={monthDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, operatingUnit, currentReport?.month, GENERAL_REPORT_CONSTANT.MONTH, generalReportVM])

    const memoProposalType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.proposalType || ''}
                fieldValue={currentReport?.proposalType}
                fieldLabel={GENERAL_REPORT_CONSTANT.PROPOSAL_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.RADIO_GROUP}
                fieldKey={'proposalType'}
                isShowMissingError={true}
                maxLength={10}
                options={proposalTypeRadioOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.proposalType, GENERAL_REPORT_CONSTANT.PROPOSAL_TYPE, generalReportVM])

    const memoVersionType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.version || ''}
                fieldValue={currentReport?.version}
                fieldLabel={GENERAL_REPORT_CONSTANT.VERSION}
                isSaveClicked={false}
                fieldType={FieldType.RADIO_GROUP}
                fieldKey={'version'}
                isShowMissingError={true}
                maxLength={10}
                options={versionTypeRadioOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.version, GENERAL_REPORT_CONSTANT.VERSION, generalReportVM])


    const memoProposalStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.proposalStatus ?? ''}
                fieldValue={currentReport?.proposalStatus}
                fieldLabel={GENERAL_REPORT_CONSTANT.PROPOSAL_STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'proposalStatus'}
                maxLength={60}
                options={approvalProcessStatusDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.proposalStatus, GENERAL_REPORT_CONSTANT.PROPOSAL_STATUS, generalReportVM])

    const memoTariffNature = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.tariffNature ?? ''}
                fieldValue={currentReport?.tariffNature}
                fieldLabel={GENERAL_REPORT_CONSTANT.TARIFF_NATURE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tariffNature'}
                maxLength={60}
                options={generalReportState.dynamicOptions.tariffNatureDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.tariffNature, GENERAL_REPORT_CONSTANT.TARIFF_NATURE, generalReportState.dynamicOptions.tariffNatureDropdownOptions, generalReportVM])

    const memoTariffType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.tariffType ?? ''}
                fieldValue={currentReport?.tariffType}
                fieldLabel={GENERAL_REPORT_CONSTANT.TARIFF_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tariffType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.tariffTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.tariffType, GENERAL_REPORT_CONSTANT.TARIFF_TYPE, generalReportState.dynamicOptions.tariffTypeDropdownOptions, generalReportVM])

    const memoVesselArrivalFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_FROM}
                width="300px"
                optional={false}
                date={currentReport?.vesselArrivalFromDate}
                fieldName="vesselArrivalFromDate"
                errorMessage={allFormState ? allFormState["vesselArrivalFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_FROM, allFormState, currentReport?.vesselArrivalFromDate, generalReportVM.onHeaderFieldChange])

    const memoVesselArrivalTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_TO}
                width="300px"
                optional={false}
                date={currentReport?.vesselArrivalToDate}
                fieldName="vesselArrivalToDate"
                errorMessage={allFormState ? allFormState["vesselArrivalToDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.VESSEL_ARRIVAL_TO, allFormState, currentReport?.vesselArrivalToDate, generalReportVM.onHeaderFieldChange])

    const memoThc = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport.thcList}
                fieldLabel={GENERAL_REPORT_CONSTANT.THC}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'thcList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.thcDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.THC, currentReport.thcList, generalReportState.dynamicOptions.thcDropdownOptions, generalReportVM])

    const memoShipmentType = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport.shipmentTypeList}
                fieldLabel={GENERAL_REPORT_CONSTANT.SHIPMENT_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'shipmentTypeList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.shipmentTypeDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.SHIPMENT_TYPE, allFormState, currentReport.shipmentTypeList, generalReportState.dynamicOptions.shipmentTypeDropdownOptions, generalReportVM])

    const memoContainerSize = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport.containerSizeList}
                fieldLabel={GENERAL_REPORT_CONSTANT.CONTAINER_SIZE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'containerSizeList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.containerSizeDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.CONTAINER_SIZE, allFormState, currentReport.containerSizeList, generalReportState.dynamicOptions.containerSizeDropdownOptions, generalReportVM])

    const memoContainerStatus = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport.containerStatusList}
                fieldLabel={GENERAL_REPORT_CONSTANT.CONTAINER_STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'containerStatusList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.containerStatusDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.CONTAINER_STATUS, currentReport.containerStatusList, generalReportState.dynamicOptions.containerStatusDropdownOptions, generalReportVM])

    const memoConsortium = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.consortiumCode ?? ''}
                fieldValue={currentReport?.consortiumCode}
                fieldLabel={currentReport?.reportType === "Receival and Delivery Charges Summary" || currentReport?.reportType === "Container Un-Billed / Billed THC Report" ?
                    GENERAL_REPORT_CONSTANT.CONSORTIUM : GENERAL_REPORT_CONSTANT.CONSORTIUM_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'consortiumCode'}
                maxLength={4}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onFieldChangeUpperCase(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.CONSORTIUM, GENERAL_REPORT_CONSTANT.CONSORTIUM_CODE, currentReport?.consortiumCode, currentReport?.reportType, generalReportVM])

    const memoVessel = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.vessleCode ?? ''}
                fieldValue={currentReport?.vessleCode}
                fieldLabel={currentReport?.reportType === "Receival and Delivery Charges Summary" || currentReport?.reportType === "Container Un-Billed / Billed THC Report" ?
                    GENERAL_REPORT_CONSTANT.VESSEL : GENERAL_REPORT_CONSTANT.VESSEL_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'vessleCode'}
                maxLength={6}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onFieldChangeUpperCase(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.VESSEL, GENERAL_REPORT_CONSTANT.VESSEL_CODE, currentReport?.reportType, currentReport?.vessleCode, generalReportVM])

    const memoVoyage = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.voyageCode ?? ''}
                fieldValue={currentReport?.voyageCode}
                fieldLabel={currentReport?.reportType === "Receival and Delivery Charges Summary" || currentReport?.reportType === "Invoice Summary Report"
                    || currentReport?.reportType === "Container Un-Billed / Billed THC Report" ? GENERAL_REPORT_CONSTANT.VOYAGE : GENERAL_REPORT_CONSTANT.VOYAGE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'voyageCode'}
                maxLength={10}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onFieldChangeUpperCase(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.VOYAGE, GENERAL_REPORT_CONSTANT.VOYAGE_CODE, currentReport?.reportType, currentReport?.voyageCode, generalReportVM])

    const memoType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["type"] : ""}
                isReadOnly={false}
                isShowOptional={generalReportState.enabledField.type ? isCompanyTypeAndTypeShowOptional() : true}
                readOnlyValue={currentReport?.type ?? ''}
                fieldValue={currentReport?.type}
                fieldLabel={GENERAL_REPORT_CONSTANT.TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'type'}
                maxLength={60}
                options={generalReportState.dynamicOptions.typeDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, generalReportState.enabledField.type, generalReportState.dynamicOptions.typeDropdownOptions, isCompanyTypeAndTypeShowOptional, currentReport?.type, GENERAL_REPORT_CONSTANT.TYPE, generalReportVM])

    const memoDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={isDateFromToShowOptional(currentReport.reportType)}
                label={GENERAL_REPORT_CONSTANT.DATE_FROM}
                width="300px"
                date={currentReport?.dateFrom}
                fieldName="dateFrom"
                errorMessage={allFormState ? allFormState["dateFrom"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DATE_FROM, allFormState, currentReport?.dateFrom, currentReport.reportType, generalReportVM.onHeaderFieldChange])

    const memoDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={isDateFromToShowOptional(currentReport.reportType)}
                label={GENERAL_REPORT_CONSTANT.DATE_TO}
                width="300px"
                date={currentReport?.dateTo}
                fieldName="dateTo"
                errorMessage={allFormState ? allFormState["dateTo"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DATE_TO, allFormState, currentReport?.dateTo, currentReport.reportType, generalReportVM.onHeaderFieldChange])

    const memoOpsLineFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.opsLineFrom ?? ''}
                fieldValue={currentReport?.opsLineFrom}
                fieldLabel={GENERAL_REPORT_CONSTANT.OPS_LINE_FROM}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLineFrom'}
                maxLength={60}
                options={generalReportState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.opsLineFrom, GENERAL_REPORT_CONSTANT.OPS_LINE_FROM, generalReportState.dynamicOptions.opsLineDropdownOptions, generalReportVM])

    const memoOpsLineTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.opsLineTo ?? ''}
                fieldValue={currentReport?.opsLineTo}
                fieldLabel={GENERAL_REPORT_CONSTANT.OPS_LINE_TO}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLineTo'}
                maxLength={60}
                options={generalReportState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.opsLineTo, GENERAL_REPORT_CONSTANT.OPS_LINE_TO, generalReportState.dynamicOptions.opsLineDropdownOptions, generalReportVM])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.billToCompany ?? ''}
                fieldValue={currentReport?.billToCompany}
                fieldLabel={GENERAL_REPORT_CONSTANT.BILL_TO_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={generalReportState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.billToCompany, GENERAL_REPORT_CONSTANT.BILL_TO_COMPANY, generalReportState.dynamicOptions.companyCodeDropdownOptions, generalReportVM])

    const memoCompanyType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["companyType"] : ""}
                isReadOnly={false}
                isShowOptional={generalReportState.enabledField.companyType ? isCompanyTypeAndTypeShowOptional() : true}
                readOnlyValue={currentReport?.companyType ?? ''}
                fieldValue={currentReport?.companyType}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.companyTypeDropdownOptions}
                sort={false}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, generalReportState.enabledField.companyType, generalReportState.dynamicOptions.companyTypeDropdownOptions, isCompanyTypeAndTypeShowOptional, currentReport?.companyType, GENERAL_REPORT_CONSTANT.COMPANY_TYPE, generalReportVM])

    const memoLpaCompanyName = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>

            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.companyNameList?.join(',')}
                fieldValue={currentReport?.companyNameList}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_NAME}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyNameList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.companyNameDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.COMPANY_NAME, currentReport.companyNameList, generalReportState.dynamicOptions.companyNameDropdownOptions, generalReportVM])

    const memoLpaCompanyCode = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>

            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.lpaCompanyCodeList?.join(',')}
                fieldValue={currentReport?.lpaCompanyCodeList}
                fieldLabel={GENERAL_REPORT_CONSTANT.COMPANY_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'lpaCompanyCodeList'}
                maxLength={60}
                isMultipleDropDown={true}
                options={generalReportState.dynamicOptions.ccCompanyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.COMPANY_CODE, currentReport.lpaCompanyCodeList, generalReportState.dynamicOptions.ccCompanyCodeDropdownOptions, generalReportVM])

    const memoNtnNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.ntnNo ?? ''}
                fieldValue={currentReport?.ntnNo}
                fieldLabel={GENERAL_REPORT_CONSTANT.NTN_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'ntnNo'}
                maxLength={15}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.NTN_NO, currentReport?.ntnNo, generalReportVM])

    const memoAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.amount?.toString() ?? ''}
                fieldValue={currentReport?.amount}
                fieldLabel={GENERAL_REPORT_CONSTANT.AMOUNT}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'amount'}
                maxLength={10}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.AMOUNT, currentReport?.amount, generalReportVM])

    const memoTransactionType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.transactionType ?? ''}
                fieldValue={currentReport?.transactionType}
                fieldLabel={GENERAL_REPORT_CONSTANT.TRANSACTION_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'transactionType'}
                maxLength={60}
                options={TransactionTypeDroOpts().getTransactionTypeModel()}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.transactionType, GENERAL_REPORT_CONSTANT.TRANSACTION_TYPE, generalReportVM])

    const memoTransactionDateTimeFrom = /* useMemo(() =>*/
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.transactionFromDate}
                fieldName="transactionFromDate"
                errorMessage={allFormState ? allFormState["transactionFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
    //, [GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_FROM, currentReport?.transactionFromDate, generalReportVM.onHeaderFieldChange])

    const memoTransactionDateTimeTo = /*useMemo(() =>*/
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.transactionTomDate}
                fieldName="transactionTomDate"
                errorMessage={allFormState ? allFormState["transactionTomDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
    // , [GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_TO, currentReport?.transactionTomDate, generalReportVM.onHeaderFieldChange])

    const memoTransactionDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.transactionFromDate}
                fieldName="transactionFromDate"
                errorMessage={allFormState ? allFormState["transactionFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_FROM, allFormState, currentReport?.transactionFromDate, generalReportVM.onHeaderFieldChange])

    const memoTransactionDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.transactionTomDate}
                fieldName="transactionTomDate"
                errorMessage={allFormState ? allFormState["transactionTomDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.TRANSACTION_DATE_TO, allFormState, currentReport?.transactionTomDate, generalReportVM.onHeaderFieldChange])

    const memoDepositAmount = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.depositAmount?.toString() ?? ''}
                fieldValue={currentReport?.depositAmount}
                fieldLabel={GENERAL_REPORT_CONSTANT.DEPOSIT_AMOUNT}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'depositAmount'}
                maxLength={10}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DEPOSIT_AMOUNT, currentReport?.depositAmount, generalReportVM])

    const memoUserId = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.userId?.toString() ?? ''}
                fieldValue={currentReport?.userId}
                fieldLabel={GENERAL_REPORT_CONSTANT.USER_ID}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'userId'}
                maxLength={30}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.USER_ID, currentReport?.userId, generalReportVM])

    const memoShipperCompanyName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.shipperConsigneeCompanyName?.toString() ?? ''}
                fieldValue={currentReport?.shipperConsigneeCompanyName}
                fieldLabel={GENERAL_REPORT_CONSTANT.SHIPPER_CONSIGNEE_COMPNY_NAME}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'shipperConsigneeCompanyName'}
                maxLength={50}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.SHIPPER_CONSIGNEE_COMPNY_NAME, currentReport?.shipperConsigneeCompanyName, generalReportVM])

    const memoShipperConsignee = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.shipperConsignee ?? ''}
                fieldValue={currentReport?.shipperConsignee}
                fieldLabel={GENERAL_REPORT_CONSTANT.SHIPPER_CONSIGNEE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'shipperConsignee'}
                maxLength={60}
                options={generalReportState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.shipperConsignee, GENERAL_REPORT_CONSTANT.SHIPPER_CONSIGNEE, generalReportState.dynamicOptions.companyCodeDropdownOptions, generalReportVM])

    const memoSubType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.subType ?? ''}
                fieldValue={currentReport?.subType}
                fieldLabel={GENERAL_REPORT_CONSTANT.SUB_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'subType'}
                maxLength={60}
                options={generalReportState.dynamicOptions.subTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.subType, GENERAL_REPORT_CONSTANT.SUB_TYPE, generalReportState.dynamicOptions.subTypeDropdownOptions, generalReportVM])

    const memoDocType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.docType ?? ''}
                fieldValue={currentReport?.docType}
                fieldLabel={GENERAL_REPORT_CONSTANT.DOC_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'docType'}
                maxLength={60}
                options={docTypeDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.docType, GENERAL_REPORT_CONSTANT.DOC_TYPE, generalReportVM])

    const memoView = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "600px", marginBottom: "20px" }}>
            <label className="sc-hXRMBi hbzIcW">{GENERAL_REPORT_CONSTANT.VIEW}</label>
            <CriteriaItemContainer>
                <HPHGroupRadioButton
                    label={""}
                    radioOptions={[
                        { inputId: 'standard', key: 'standardView', name: 'Standard' },
                    ]}
                    value={[
                        { inputId: 'standard', key: 'standardView', name: 'Standard' }
                    ].find(item => currentReport.standardView === 'Y')?.key ?? "others"}
                    onChange={(e) => generalReportVM.onRadioChange(e, 'standardView')}
                    checked={[
                        { inputId: 'standard', key: 'standardView', name: 'Standard' },
                    ].find(item => currentReport.standardView === 'Y')?.name ?? "others"}
                />
                <HPHGroupRadioButton
                    label={""}
                    radioOptions={[
                        { inputId: 'credit', key: 'creditView', name: 'Credit' }
                    ]}
                    value={[
                        { inputId: 'credit', key: 'creditView', name: 'Credit' }
                    ].find(item => currentReport.creditView === 'Y')?.key ?? "others"}
                    onChange={(e) => generalReportVM.onRadioChange(e, 'creditView')}
                    checked={[
                        { inputId: 'credit', key: 'creditView', name: 'Credit' }
                    ].find(item => currentReport.creditView === 'Y')?.name ?? "others"}
                />
                <HPHGroupRadioButton
                    label={""}
                    radioOptions={[
                        { inputId: 'adjustment', key: 'adjustmentView', name: 'Adjustment' }
                    ]}
                    value={[
                        { inputId: 'adjustment', key: 'adjustmentView', name: 'Adjustment' }
                    ].find(item => currentReport.adjustmentView === 'Y')?.key ?? "others"}
                    onChange={(e) => generalReportVM.onRadioChange(e, 'adjustmentView')}
                    checked={[
                        { inputId: 'adjustment', key: 'adjustmentView', name: 'Adjustment' }
                    ].find(item => currentReport.adjustmentView === 'Y')?.name ?? "others"}
                />
            </CriteriaItemContainer>
        </div>
        , [GENERAL_REPORT_CONSTANT.VIEW, currentReport.adjustmentView, currentReport.creditView, currentReport.standardView, generalReportVM])

    const memoMasterChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.masterChargeOnCompany ?? ''}
                fieldValue={currentReport?.masterChargeOnCompany}
                fieldLabel={GENERAL_REPORT_CONSTANT.MASTER_CHARGE_ON_COMPANY}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'masterChargeOnCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={generalReportState.dynamicOptions.masterCompanyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.masterChargeOnCompany, GENERAL_REPORT_CONSTANT.MASTER_CHARGE_ON_COMPANY, generalReportState.dynamicOptions.masterCompanyCodeDropdownOptions, generalReportVM])

    const memoOperatingTml = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.operatingTml ?? ''}
                fieldValue={currentReport?.operatingTml}
                fieldLabel={GENERAL_REPORT_CONSTANT.OPERATING_TML}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'operatingTml'}
                maxLength={60}
                options={generalReportState.dynamicOptions.operatingTmlDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.operatingTml, GENERAL_REPORT_CONSTANT.OPERATING_TML, generalReportState.dynamicOptions.operatingTmlDropdownOptions, generalReportVM])

    const memoServiceCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.serviceCode ?? ''}
                fieldValue={currentReport?.serviceCode}
                fieldLabel={GENERAL_REPORT_CONSTANT.SERVICE_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceCode'}
                maxLength={60}
                options={generalReportState.dynamicOptions.serviceCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.serviceCode, GENERAL_REPORT_CONSTANT.SERVICE_CODE, generalReportState.dynamicOptions.serviceCodeDropdownOptions, generalReportVM])

    const memoReferenceDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.REFERENCE_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.referenceFromDate}
                fieldName="referenceFromDate"
                errorMessage={allFormState ? allFormState["referenceFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.REFERENCE_DATE_FROM, allFormState, currentReport?.referenceFromDate, generalReportVM.onHeaderFieldChange])

    const memoReferenceDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.REFERENCE_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.referenceTomDate}
                fieldName="referenceTomDate"
                errorMessage={allFormState ? allFormState["referenceTomDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.REFERENCE_DATE_TO, allFormState, currentReport?.referenceTomDate, generalReportVM.onHeaderFieldChange])

    const memoApprovedDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.APPROVED_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.approvedFromDate}
                fieldName="approvedFromDate"
                errorMessage={allFormState ? allFormState["approvedFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.APPROVED_DATE_FROM, allFormState, currentReport?.approvedFromDate, generalReportVM.onHeaderFieldChange])

    const memoApprovedDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.APPROVED_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.approvedTomDate}
                fieldName="approvedTomDate"
                errorMessage={allFormState ? allFormState["approvedTomDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.APPROVED_DATE_TO, allFormState, currentReport?.approvedTomDate, generalReportVM.onHeaderFieldChange])

    const memoOpsDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DatePickerComponent
                disabled={false}
                optional={true}
                label={GENERAL_REPORT_CONSTANT.OPS_DATE}
                width="300px"
                date={currentReport?.opsDate}
                fieldName="opsDate"
                errorMessage={""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.OPS_DATE, currentReport?.opsDate, generalReportVM.onHeaderFieldChange])

    //AQCT/OICT/RSCT
    const memoSettlementDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.settlementFromDate}
                fieldName="settlementFromDate"
                errorMessage={allFormState ? allFormState["settlementFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_FROM, allFormState, currentReport?.settlementFromDate, generalReportVM.onHeaderFieldChange])

    const memoSettlementDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.settlementToDate}
                fieldName="settlementToDate"
                errorMessage={allFormState ? allFormState["settlementToDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.SETTLEMENT_DATE_TO, allFormState, currentReport?.settlementToDate, generalReportVM.onHeaderFieldChange])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.opsLine ?? ''}
                fieldValue={currentReport?.opsLine}
                fieldLabel={GENERAL_REPORT_CONSTANT.OPS_LINE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLine'}
                maxLength={60}
                options={generalReportState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [currentReport?.opsLine, GENERAL_REPORT_CONSTANT.OPS_LINE, generalReportState.dynamicOptions.opsLineDropdownOptions, generalReportVM])

    const memoDateTimeFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.dateFrom}
                fieldName="dateFrom"
                errorMessage={allFormState ? allFormState["dateFrom"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DATE_FROM, allFormState, currentReport?.dateFrom, generalReportVM.onHeaderFieldChange])

    const memoDateTimeTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.dateTo}
                fieldName="dateTo"
                errorMessage={allFormState ? allFormState["dateTo"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.DATE_TO, allFormState, currentReport?.dateTo, generalReportVM.onHeaderFieldChange])


    const memoColltionDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.COLL_DATE_FROM}
                width="300px"
                optional={false}
                date={currentReport?.collFromDate}
                fieldName="collFromDate"
                errorMessage={allFormState ? allFormState["collFromDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.COLL_DATE_FROM, allFormState, currentReport?.collFromDate, generalReportVM.onHeaderFieldChange])

    const memoColltionDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <DateTimePickerComponent
                disabled={false}
                label={GENERAL_REPORT_CONSTANT.COLL_DATE_TO}
                width="300px"
                optional={false}
                date={currentReport?.collToDate}
                fieldName="collToDate"
                errorMessage={allFormState ? allFormState["collToDate"] : ""}
                onDateChange={generalReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_REPORT_CONSTANT.COLL_DATE_TO, allFormState, currentReport?.collToDate, generalReportVM.onHeaderFieldChange])

    const memoCashier = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport?.cashierList}
                fieldLabel={GENERAL_REPORT_CONSTANT.CASHIER_ID}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                isMultipleDropDown={true}
                fieldKey={'cashierList'}
                maxLength={60}
                options={generalReportState.dynamicOptions.cashierDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.CASHIER_ID, currentReport.cashierList, generalReportState.dynamicOptions.cashierDropdownOptions, generalReportVM])

    const memoShippingLine = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport?.shippingLineList}
                fieldLabel={GENERAL_REPORT_CONSTANT.SHIPPING_LINE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                isMultipleDropDown={true}
                fieldKey={'shippingLineList'}
                maxLength={60}
                options={generalReportState.dynamicOptions.slCompanyCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.SHIPPING_LINE, currentReport?.shippingLineList, generalReportState.dynamicOptions.slCompanyCodeDropdownOptions, generalReportVM])

    const memoTariffCode = useMemo(() =>
        <div className='flex-row-item' style={{ width: "300px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                fieldValue={currentReport?.tariffCodeList}
                fieldLabel={GENERAL_REPORT_CONSTANT.TARIFF_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                isMultipleDropDown={true}
                fieldKey={'tariffCodeList'}
                maxLength={60}
                options={generalReportState.dynamicOptions.tariffCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue as string[], fFullValue)} />
        </div>
        , [GENERAL_REPORT_CONSTANT.TARIFF_CODE, currentReport.tariffCodeList, generalReportState.dynamicOptions.tariffCodeDropdownOptions, generalReportVM])

    return <>

        <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
            <HeaderTitle>{GeneralReportConstant.Title.TITLE}</HeaderTitle>
            {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
            <StyledAction className="tm-animated-wrapper">
                <HPHButton label={WorkspaceConstant.Common.BUTTON_CLEAR} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleReset} />
                {allowRead && <><div className="add-seperator" />
                    <HPHButton label={WorkspaceConstant.Common.BUTTON_PRINT} size={"Small"} theme={"Secondary"} disabled={false} onClick={handlePrint} />
                    <OverflowMenuButton disabled={false} buttonLabel={WorkspaceConstant.Common.BUTTON_EXPORT_AS} navigationList={memoExportMenu} /></>}
                {allowRead && <HPHButton label={WorkspaceConstant.Common.BUTTON_VIEW} size={"Small"} theme={"Primary"} disabled={false} onClick={handleView} />}
                {allowRead && <OverflowMenuButton buttonTheme={"Primary"} disabled={false} buttonLabel={WorkspaceConstant.Common.BUTTON_DOWNLOAD_AS} navigationList={memoDownloadMenu} />}
            </StyledAction>
        </Sidebarheader>
        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoReportType}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoPrinter}
                            {memoFileLocation}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.view ? memoView : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.date && !isDateFieldDisable() ? memoDateFrom : null}
                            {generalReportState.enabledField.date && !isDateFieldDisable() ? memoDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.proposalType ? memoProposalType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.proposalFromTo ? memoCompanyCodeFrom : null}
                            {generalReportState.enabledField.proposalFromTo ? memoProposalFrom : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.proposalFromTo ? memoCompanyCodeTo : null}
                            {generalReportState.enabledField.proposalFromTo ? memoProposalTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.exceptionType ? memoExceptionDateFrom : null}
                            {generalReportState.enabledField.exceptionType ? memoExceptionDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.exceptionType ? memoExceptionType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.tariff ? memoTariffNature : null}
                            {generalReportState.enabledField.tariff ? memoTariffType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.ediView ? memoEdiView : null}
                            {generalReportState.enabledField.companyCode && !isFieldDisable() ? memoCompanyCode : null}
                            {generalReportState.enabledField.customerCode ? memoCustomerCode : null}
                            {generalReportState.enabledField.lpaCompanyCode ? memoLpaCompanyCode : null}
                            {generalReportState.enabledField.companyName ? memoLpaCompanyName : null}
                            {generalReportState.enabledField.activeInd && !isSpecialFieldDisable() ? memoActiveInd : null}
                            {generalReportState.enabledField.proposalNo && !isFieldDisable() ? memoProposalNo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.docNo ? memoDocNoFrom : null}
                            {generalReportState.enabledField.docNo ? memoDocNoTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.criteriaType ? memoCriteriaType : null}
                            {generalReportState.enabledField.criteriaName ? memoCriteriaName : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.docDate ? memoDocDateFrom : null}
                            {generalReportState.enabledField.docDate ? memoDocDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.vesselArrivalDate ? memoVesselArrivalFrom : null}
                            {generalReportState.enabledField.vesselArrivalDate ? memoVesselArrivalTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.thc ? memoThc : null}
                            {generalReportState.enabledField.shipmentType ? memoShipmentType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.containerSize ? memoContainerSize : null}
                            {generalReportState.enabledField.containerStatus ? memoContainerStatus : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.glDate ? memoGlDateFrom : null}
                            {generalReportState.enabledField.glDate ? memoGlDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.shipperConsignee ? memoShipperConsignee : null}
                            {generalReportState.enabledField.shipperConsigneeCompanyName ? memoShipperCompanyName : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.consortiumCode && !isConsortiumFieldDisable() ? memoConsortium : null}
                            {generalReportState.enabledField.vessleCode ? memoVessel : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.subType ? memoSubType : null}
                            {generalReportState.enabledField.voyageCode ? memoVoyage : null}
                            {generalReportState.enabledField.consortiumCode && !isConsortiumSpecialFieldDisable() ? memoConsortium : null}
                            {generalReportState.enabledField.companyCode && !isCompanyCodeFieldDisable() ? memoCompanyCode : null}
                            {generalReportState.enabledField.billToCompany ? memoBillToCompany : null}
                            {generalReportState.enabledField.masterChargeOnCompany ? memoMasterChargeOnCompany : null}
                            {generalReportState.enabledField.chargeOnCompany && (operatingUnit === "SAPT" || operatingUnit === "KICT") ? memoChargeOnCompany : null}
                            {/* AQCT/OICT/RSCT */}
                            {generalReportState.enabledField.opsLine && (operatingUnit !== "SAPT" && operatingUnit !== "KICT") ? memoOpsLine : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.operatingTml ? memoOperatingTml : null}
                            {generalReportState.enabledField.serviceCode ? memoServiceCode : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.opsDate && !isFieldDisable() ? memoOpsDateFrom : null}
                            {generalReportState.enabledField.opsDate && !isFieldDisable() ? memoOpsDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.opsLine && (operatingUnit === "SAPT" || operatingUnit === "KICT") ? memoOpsLineFrom : null}
                            {generalReportState.enabledField.opsLine && (operatingUnit === "SAPT" || operatingUnit === "KICT") ? memoOpsLineTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.yearInput ? memoYear : null}
                            {generalReportState.enabledField.month ? memoMonth : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.chargeOnCompany && (operatingUnit !== "SAPT" && operatingUnit !== "KICT") ? memoChargeOnCompany : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.chargeType ? memoChargeType : null}
                            {generalReportState.enabledField.masterCompanyCode ? memoMasterCompanyCode : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.effectiveDate ? memoEffectiveDateFrom : null}
                            {generalReportState.enabledField.effectiveDate ? memoEffectiveDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.expiryDate ? memoExpiryDateFrom : null}
                            {generalReportState.enabledField.expiryDate ? memoExpiryDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.version && !isFieldDisable() ? memoVersionType : null}
                            {generalReportState.enabledField.proposalStatus ? memoProposalStatus : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.companyType ? memoCompanyType : null}
                        </CriteriaItemContainer>

                        {/* AQCT/OICT/RSCT */}
                        <CriteriaItemContainer>
                            {generalReportState.enabledField.dateTimeFromTo ? memoDateTimeFrom : null}
                            {generalReportState.enabledField.dateTimeFromTo ? memoDateTimeTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.type ? memoType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.ntnNo ? memoNtnNo : null}
                            {generalReportState.enabledField.amount ? memoAmount : null}
                            {generalReportState.enabledField.depositAmount ? memoDepositAmount : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.transactionType ? memoTransactionType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.transactionDate ? (isDateTime() ? memoTransactionDateTimeFrom : memoTransactionDateFrom) : null}
                            {generalReportState.enabledField.transactionDate ? (isDateTime() ? memoTransactionDateTimeTo : memoTransactionDateTo) : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.opsDate && !isSpecialFieldDisable() ? memoOpsDate : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.docType ? memoDocType : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.userId ? memoUserId : null}
                            {generalReportState.enabledField.activeInd && !isActiveIndFieldDisable() ? memoActiveInd : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.referenceDate ? memoReferenceDateFrom : null}
                            {generalReportState.enabledField.referenceDate ? memoReferenceDateTo : null}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {generalReportState.enabledField.approvedDate ? memoApprovedDateFrom : null}
                            {generalReportState.enabledField.approvedDate ? memoApprovedDateTo : null}
                        </CriteriaItemContainer>

                        {/* AQCT/OICT/RSCT */}
                        <CriteriaItemContainer>
                            {generalReportState.enabledField.settlementDate ? memoSettlementDateFrom : null}
                            {generalReportState.enabledField.settlementDate ? memoSettlementDateTo : null}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {generalReportState.enabledField.collDate ? memoColltionDateFrom : null}
                            {generalReportState.enabledField.collDate ? memoColltionDateTo : null}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {generalReportState.enabledField.cashier ? memoCashier : null}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {generalReportState.enabledField.shippingLine ? memoShippingLine : null}
                            {generalReportState.enabledField.tariffCode ? memoTariffCode : null}
                        </CriteriaItemContainer>
                        {/*<CriteriaItemContainer>
                            {generalReportState.enabledField.tariffCode ? memoTariffCode : null}
                        </CriteriaItemContainer>*/}

                    </div>
                </div>
            </div>
        </StyledSidebar >
    </>
}

export default memo(GeneralReportPanel);
